
/* ── fill line helper ─────────────────────────────── */
const FL = ({ value = '', flex = false, minWidth = 80 }) => (
  <span style={{
    borderBottom: '1px solid #333',
    display: 'inline-block',
    minWidth: flex ? undefined : minWidth,
    flex: flex ? 1 : undefined,
    paddingBottom: 1,
    lineHeight: 1.4,
    verticalAlign: 'bottom'
  }}>{value || '\u00A0'}</span>
);

/* ── dotted line ────────────────────────────────────── */
const DotLine = ({ minHeight = 18 }) => (
  <div style={{ borderBottom: '1px dotted #555', minHeight, marginBottom: 2 }}>&nbsp;</div>
);

function OPDSlipPrint({ patient, visitInfo, vitals, consultationInfo }) {
  const now = new Date();
  const consultDate = now.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const age = patient?.dateOfBirth
    ? Math.floor((now - new Date(patient.dateOfBirth)) / (365.25 * 24 * 3600 * 1000))
    : (patient?.date_of_birth
      ? Math.floor((now - new Date(patient.date_of_birth)) / (365.25 * 24 * 3600 * 1000))
      : '');
  const sex = patient?.gender ? patient.gender.charAt(0).toUpperCase() : '';
  const ageSex = age !== '' ? `${age} / ${sex}` : sex ? `/ ${sex}` : '';

  const fullName = patient
    ? `${patient.firstName || patient.first_name || ''} ${patient.lastName || patient.last_name || ''}`.trim()
    : '';

  const chiefComplaint = patient?.chiefComplaint || patient?.chief_complaint
    || visitInfo?.chiefComplaint || '';
  const diagnosis      = consultationInfo?.diagnosis || '';
  const notes          = consultationInfo?.notes || '';
  const doctorName     = consultationInfo?.doctorName || '';
  const treatments     = consultationInfo?.treatments || [];
  const packages       = consultationInfo?.packages || [];

  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      color: '#111',
      width: '210mm',
      minHeight: '297mm',
      padding: '8mm 10mm',
      boxSizing: 'border-box',
      backgroundColor: '#fff',
      display: 'flex',
      flexDirection: 'column'
    }}>

      {/* ── Header ──────────────────────────────────── */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 4 }}>
        <tbody>
          <tr>
            <td style={{ width: 80, verticalAlign: 'middle', paddingRight: 8 }}>
              <img src="/logo.png" alt="MSG Logo"
                style={{ width: 72, height: 72, objectFit: 'contain' }} />
            </td>
            <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#1b5e20', letterSpacing: 0.5 }}>
                MSG NATUROPATHY CENTER
              </div>
              <div style={{ fontSize: 11, color: '#2e7d32', marginTop: 2 }}>
                Shah Satnam ji Speciality Hospitals, Sirsa, Haryana (125055)
              </div>
              <div style={{ fontSize: 11, color: '#111', marginTop: 2 }}>
                +91 70151 60076, +91 99968 50801, +91 7082999551, +91 7082999351
              </div>
            </td>
            <td style={{ width: 80 }}></td>
          </tr>
        </tbody>
      </table>

      {/* ── OPD Title + Date ────────────────────────── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
        borderTop: '2px solid #1b5e20',
        borderBottom: '2px solid #1b5e20',
        padding: '4px 0',
        marginBottom: 8
      }}>
        <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: 700 }}>Date:</span>
          <FL value={consultDate} minWidth={120} />
        </div>
      </div>

      {/* ── Patient Info ─────────────────────────────── */}
      <div style={{ fontSize: 12, lineHeight: 2.0, marginBottom: 6 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, flex: 2 }}>
            <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Name:</span>
            <FL value={fullName} flex minWidth={120} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, flex: 1 }}>
            <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Age/sex:</span>
            <FL value={ageSex} minWidth={60} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, flex: 1.5 }}>
            <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Consultant:</span>
            <FL value={doctorName} flex minWidth={80} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, flex: 2 }}>
            <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Address:</span>
            <FL value={patient?.address || ''} flex minWidth={140} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, flex: 1 }}>
            <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>OPD No:</span>
            <FL value={visitInfo?.opdId || ''} minWidth={60} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, flex: 1.5 }}>
            <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>UHID:</span>
            <FL value={patient?.patientCode || patient?.patient_code || ''} flex minWidth={80} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
          <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Mobile No:</span>
          <FL value={patient?.phone || ''} minWidth={160} />
        </div>
      </div>

      {/* ── Body: left vitals col + right writing area ── */}
      <div style={{
        display: 'flex',
        flex: 1,
        border: '1.5px solid #333',
        minHeight: '170mm'
      }}>
        {/* Left column — vitals + investigations */}
        <div style={{
          width: '38mm',
          borderRight: '1.5px solid #333',
          padding: '10px 8px',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {[
            ['BP',     vitals?.bloodPressure   || ''],
            ['Pulse',  vitals?.pulseRate        || ''],
            ['Weight', vitals?.weight ? `${vitals.weight} kg` : ''],
            ['Height', vitals?.height ? `${vitals.height} cm` : ''],
            ['Temp',   vitals?.temperature      || ''],
            ['SpO2',   vitals?.oxygenSaturation || ''],
          ].map(([label, val]) => (
            <div key={label} style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#1b5e20' }}>{label}</div>
              <div style={{ borderBottom: '1px solid #333', minHeight: 16, fontSize: 11, paddingBottom: 1 }}>
                {val || '\u00A0'}
              </div>
            </div>
          ))}

          <div style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: '#1b5e20', marginBottom: 4 }}>
              Investigations
            </div>
            {/* Show diagnosis in investigations if present */}
            {diagnosis ? (
              <div style={{ fontSize: 10, whiteSpace: 'pre-wrap', marginBottom: 4 }}>{diagnosis}</div>
            ) : (
              <>
                <DotLine /><DotLine /><DotLine /><DotLine />
              </>
            )}
            <DotLine /><DotLine /><DotLine /><DotLine />
          </div>
        </div>

        {/* Right column — diagnosis, chief complaint, treatments, packages, notes */}
        <div style={{ flex: 1, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 0 }}>

          {/* Chief complaint */}
          {chiefComplaint && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 11, color: '#1b5e20', marginBottom: 2 }}>
                Chief Complaint:
              </div>
              <div style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>{chiefComplaint}</div>
            </div>
          )}

          {/* Prescribed Treatments */}
          {treatments.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 11, color: '#1b5e20', marginBottom: 3 }}>
                Prescribed Treatments:
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #999' }}>
                    <th style={{ textAlign: 'left', padding: '2px 4px', fontWeight: 700 }}>#</th>
                    <th style={{ textAlign: 'left', padding: '2px 4px', fontWeight: 700 }}>Treatment</th>
                    <th style={{ textAlign: 'center', padding: '2px 4px', fontWeight: 700 }}>Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {treatments.map((t, i) => (
                    <tr key={i} style={{ borderBottom: '1px dotted #ccc' }}>
                      <td style={{ padding: '2px 4px' }}>{i + 1}</td>
                      <td style={{ padding: '2px 4px' }}>{t.treatmentName}</td>
                      <td style={{ textAlign: 'center', padding: '2px 4px' }}>{t.quantity || 1}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Packages */}
          {packages.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 11, color: '#1b5e20', marginBottom: 3 }}>
                Packages:
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #999' }}>
                    <th style={{ textAlign: 'left', padding: '2px 4px', fontWeight: 700 }}>#</th>
                    <th style={{ textAlign: 'left', padding: '2px 4px', fontWeight: 700 }}>Package</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px dotted #ccc' }}>
                      <td style={{ padding: '2px 4px' }}>{i + 1}</td>
                      <td style={{ padding: '2px 4px' }}>{p.packageName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Notes */}
          {notes ? (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 11, color: '#1b5e20', marginBottom: 2 }}>Notes:</div>
              <div style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>{notes}</div>
            </div>
          ) : (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 11, color: '#1b5e20', marginBottom: 2 }}>Notes:</div>
              <DotLine /><DotLine /><DotLine />
            </div>
          )}

          {/* Remaining blank writing space */}
          <div style={{ flex: 1 }} />

          {/* Doctor signature */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ borderBottom: '1px solid #333', width: '50mm', marginBottom: 3 }}>&nbsp;</div>
              <div style={{ fontSize: 10 }}>Doctor's Signature</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────── */}
      <div style={{
        textAlign: 'center',
        marginTop: 8,
        paddingTop: 6,
        borderTop: '1.5px solid #1b5e20'
      }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#1b5e20' }}>
          Not Valid for Medico - Legal
        </span>
      </div>
    </div>
  );
}

export default OPDSlipPrint;
